import { acquireGraphAccessTokenSilent } from '@/lib/session-auth';

const GRAPH_USER_PHOTO_ENDPOINT = (userIdOrUpn: string): string =>
  `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(userIdOrUpn)}/photo/$value`;

const GRAPH_USER_READ_SCOPE = ['User.ReadBasic.All'];

type PhotoState =
  | { readonly status: 'pending'; readonly promise: Promise<string | null> }
  | { readonly status: 'resolved'; readonly url: string | null };

const cache = new Map<string, PhotoState>();

function normalizeKey(userIdOrUpn: string): string {
  return userIdOrUpn.trim().toLowerCase();
}

async function fetchPhotoBlobUrl(userIdOrUpn: string): Promise<string | null> {
  const token = await acquireGraphAccessTokenSilent(GRAPH_USER_READ_SCOPE);
  if (token === null) return null;
  let response: Response;
  try {
    response = await fetch(GRAPH_USER_PHOTO_ENDPOINT(userIdOrUpn), {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return null;
  }
  if (!response.ok) {
    return null;
  }
  try {
    const blob = await response.blob();
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Resolve a Microsoft Graph profile photo for another user by UPN (email) or
 * Graph object id. Results are cached per key for the lifetime of the page;
 * failures and missing photos are cached as `null` so we never retry them.
 *
 * Requires the `User.ReadBasic.All` delegated scope. In dev (backend-oidc) the
 * underlying token acquisition returns `null`, so this helper resolves to
 * `null` without performing any network request.
 */
export function fetchGraphUserPhotoUrl(userIdOrUpn: string): Promise<string | null> {
  const key = normalizeKey(userIdOrUpn);
  if (key.length === 0) return Promise.resolve(null);

  const entry = cache.get(key);
  if (entry !== undefined) {
    return entry.status === 'resolved' ? Promise.resolve(entry.url) : entry.promise;
  }

  const promise = fetchPhotoBlobUrl(key).then((url) => {
    cache.set(key, { status: 'resolved', url });
    return url;
  });
  cache.set(key, { status: 'pending', promise });
  return promise;
}

export function resetGraphUserPhotoCacheForTesting(): void {
  for (const entry of cache.values()) {
    if (entry.status === 'resolved' && entry.url !== null) {
      URL.revokeObjectURL(entry.url);
    }
  }
  cache.clear();
}
